import jsPDF from 'jspdf';

// Draws a pixel-perfect, non-overlapping Assessment Report on a 1200×1600 canvas.
// Returns the canvas (caller can toBlob for PNG, or use with jsPDF for PDF).
export const renderReportToCanvas = ({ learnerName, score, total, percent, grade,
  categoryBreakdown, timeTakenMin, timeTakenSec, dateIso, assessmentId, trackLabel }) => {
  const W = 1200;
  const H = 1600;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#050505';
  ctx.fillRect(0, 0, W, H);

  // Decorative top band
  const grad = ctx.createLinearGradient(0, 0, W, 0);
  grad.addColorStop(0, '#FFB800');
  grad.addColorStop(0.5, '#FF8800');
  grad.addColorStop(1, '#EC4899');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, 10);

  // Outer border
  ctx.strokeStyle = '#FFB800';
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = 'rgba(255,184,0,0.3)';
  ctx.lineWidth = 1;
  ctx.strokeRect(55, 55, W - 110, H - 110);

  // Header
  ctx.fillStyle = '#FFB800';
  ctx.font = 'bold 14px "JetBrains Mono", monospace';
  ctx.textAlign = 'center';
  ctx.fillText('DSA WITH KAPIL · OFFICIAL ASSESSMENT REPORT', W / 2, 110);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 52px "Outfit", "Arial Black", sans-serif';
  ctx.fillText('Final Placement Readiness', W / 2, 180);
  ctx.fillText('Assessment', W / 2, 240);

  // Subtitle
  ctx.fillStyle = '#A1A1AA';
  ctx.font = '20px "IBM Plex Sans", Arial, sans-serif';
  ctx.fillText(`${trackLabel || 'Java'} Track · Industry-oriented · ${total} MCQs · 60 Minutes`, W / 2, 285);

  // Divider
  ctx.strokeStyle = 'rgba(255,255,255,0.1)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(100, 320);
  ctx.lineTo(W - 100, 320);
  ctx.stroke();

  // "This is to certify that"
  ctx.fillStyle = '#71717A';
  ctx.font = 'italic 18px "IBM Plex Sans", Arial, sans-serif';
  const passed = percent >= 40;
  ctx.fillText(passed ? 'This is to certify that' : 'Assessment report for', W / 2, 370);

  // Learner name
  ctx.fillStyle = '#FFB800';
  ctx.font = 'bold 56px "Outfit", "Arial Black", sans-serif';
  const nameText = (learnerName || 'Learner').toUpperCase();
  let nameSize = 56;
  while (nameSize > 24) {
    ctx.font = `bold ${nameSize}px "Outfit", "Arial Black", sans-serif`;
    if (ctx.measureText(nameText).width <= W - 280) break;
    nameSize -= 2;
  }
  ctx.fillText(nameText, W / 2, 440);

  // Underline for name
  const nameMetrics = ctx.measureText(nameText);
  const nameW = Math.min(nameMetrics.width, W - 240);
  ctx.strokeStyle = '#FFB800';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo((W - nameW) / 2 - 20, 460);
  ctx.lineTo((W + nameW) / 2 + 20, 460);
  ctx.stroke();

  // Statement
  ctx.fillStyle = '#A1A1AA';
  ctx.font = '18px "IBM Plex Sans", Arial, sans-serif';
  ctx.fillText(passed ? 'has successfully completed the Final Placement Readiness Assessment' : 'has attempted the Final Placement Readiness Assessment', W / 2, 500);
  ctx.fillText('covering 250 industry-oriented MCQs across 11 technical domains.', W / 2, 528);

  // Score card
  const cardY = 580;
  const cardH = 170;
  ctx.fillStyle = '#0F0F12';
  ctx.strokeStyle = '#FFB800';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(100, cardY, W - 200, cardH, 16);
  ctx.fill();
  ctx.stroke();

  // Score big number
  ctx.fillStyle = '#FFB800';
  ctx.font = 'bold 72px "Outfit", "Arial Black", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`${score}`, 290, cardY + 105);
  ctx.fillStyle = '#71717A';
  ctx.font = 'bold 26px "JetBrains Mono", monospace';
  ctx.fillText(`/ ${total}`, 290, cardY + 145);
  ctx.font = '14px "JetBrains Mono", monospace';
  ctx.fillStyle = '#71717A';
  ctx.fillText('SCORE', 290, cardY + 40);

  // Percent
  ctx.fillStyle = '#007AFF';
  ctx.font = 'bold 72px "Outfit", "Arial Black", sans-serif';
  ctx.fillText(`${percent}%`, 600, cardY + 100);
  ctx.font = '14px "JetBrains Mono", monospace';
  ctx.fillStyle = '#71717A';
  ctx.fillText('PERCENTAGE', 600, cardY + 40);

  // Grade
  ctx.fillStyle = '#EC4899';
  ctx.font = 'bold 72px "Outfit", "Arial Black", sans-serif';
  ctx.fillText(grade, 910, cardY + 100);
  ctx.font = '14px "JetBrains Mono", monospace';
  ctx.fillStyle = '#71717A';
  ctx.fillText('GRADE', 910, cardY + 40);

  // Category breakdown header
  const catY = 800;
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 24px "Outfit", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Category Breakdown', 100, catY);

  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.moveTo(100, catY + 12);
  ctx.lineTo(W - 100, catY + 12);
  ctx.stroke();

  // Breakdown rows
  const rowH = 44;
  categoryBreakdown.forEach((cat, i) => {
    const y = catY + 50 + i * rowH;
    // Label
    ctx.fillStyle = '#A1A1AA';
    ctx.font = '16px "IBM Plex Sans", Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(cat.label, 110, y);

    // Progress bar
    const barX = 480;
    const barY = y - 14;
    const barW = 440;
    const barH = 18;
    ctx.fillStyle = '#1A1A20';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, 9);
    ctx.fill();

    const pct = cat.total ? cat.correct / cat.total : 0;
    const fillW = Math.max(4, barW * pct);
    // Color by percentage
    let barColor = '#EF4444';
    if (pct >= 0.75) barColor = '#22C55E';
    else if (pct >= 0.5) barColor = '#FFB800';
    else if (pct >= 0.25) barColor = '#F97316';

    ctx.fillStyle = barColor;
    ctx.beginPath();
    ctx.roundRect(barX, barY, fillW, barH, 9);
    ctx.fill();

    // Numbers
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px "JetBrains Mono", monospace';
    ctx.textAlign = 'right';
    ctx.fillText(`${cat.correct}/${cat.total}`, W - 110, y);

    // Percent (small)
    ctx.fillStyle = '#71717A';
    ctx.font = '12px "JetBrains Mono", monospace';
    ctx.fillText(`${Math.round(pct * 100)}%`, barX + barW + 48, y);
  });

  // Footer meta
  const fy = H - 205;
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.moveTo(100, fy);
  ctx.lineTo(W - 100, fy);
  ctx.stroke();

  const dateStr = new Date(dateIso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  ctx.textAlign = 'left';
  ctx.fillStyle = '#71717A';
  ctx.font = '12px "JetBrains Mono", monospace';
  ctx.fillText('ASSESSMENT ID', 110, fy + 30);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px "JetBrains Mono", monospace';
  ctx.fillText(assessmentId, 110, fy + 52);

  ctx.fillStyle = '#71717A';
  ctx.font = '12px "JetBrains Mono", monospace';
  ctx.fillText('DATE', 110, fy + 82);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px "JetBrains Mono", monospace';
  ctx.fillText(dateStr, 110, fy + 104);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#71717A';
  ctx.font = '12px "JetBrains Mono", monospace';
  ctx.fillText('TIME TAKEN', W / 2, fy + 30);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px "JetBrains Mono", monospace';
  ctx.fillText(timeTakenSec != null ? `${Math.floor(timeTakenSec / 60)} min ${timeTakenSec % 60} sec` : `${timeTakenMin} min`, W / 2, fy + 52);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#A1A1AA';
  ctx.font = 'italic 20px "IBM Plex Sans", Arial, sans-serif';
  ctx.fillText('Kapil', W - 110, fy + 56);
  ctx.strokeStyle = 'rgba(255,184,0,0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W - 220, fy + 66);
  ctx.lineTo(W - 110, fy + 66);
  ctx.stroke();
  ctx.fillStyle = '#71717A';
  ctx.font = '11px "JetBrains Mono", monospace';
  ctx.fillText('INSTRUCTOR · DSA WITH KAPIL', W - 110, fy + 86);

  // Watermark/footer
  ctx.textAlign = 'center';
  ctx.fillStyle = '#52525B';
  ctx.font = '11px "JetBrains Mono", monospace';
  ctx.fillText('dsa.kapil.learn · industry-grade placement benchmark', W / 2, H - 70);

  return canvas;
};

export const downloadPNG = async (reportData) => {
  const canvas = renderReportToCanvas(reportData);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Final_Assessment_${(reportData.trackLabel || 'Java').replace('++', 'PP')}_${(reportData.learnerName || 'Learner').replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
};

// jsPDF core fonts only support WinAnsi; map unsupported glyphs to safe ASCII.
const pdfSafe = (t) => String(t ?? '')
  .replace(/[\u2014\u2013]/g, '-')
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201C\u201D]/g, '"')
  .replace(/\u2192/g, '->').replace(/\u2190/g, '<-')
  .replace(/\u2264/g, '<=').replace(/\u2265/g, '>=').replace(/\u2260/g, '!=')
  .replace(/\u00B7|\u2022/g, '-')
  .replace(/\u2026/g, '...')
  .replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\u00FF]/g, '?');

const PAGE_W = 210;
const PAGE_H = 297;
const M = 14;
const CONTENT_W = PAGE_W - M * 2;
const TOP = 26;
const BOTTOM = PAGE_H - 18;

const drawReviewPage = (pdf, reportData) => {
  pdf.setFillColor(255, 184, 0);
  pdf.rect(0, 0, PAGE_W, 3, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(20, 20, 20);
  pdf.text('Answer Review', M, 14);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(110, 110, 110);
  pdf.text(pdfSafe(`${reportData.learnerName} - ${reportData.trackLabel || 'Java'} - ${reportData.assessmentId}`), PAGE_W - M, 14, { align: 'right' });
  pdf.setDrawColor(220, 220, 220);
  pdf.line(M, 18, PAGE_W - M, 18);
};

const buildBlock = (pdf, item) => {
  const qW = CONTENT_W - 12;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  const qLines = pdf.splitTextToSize(pdfSafe(item.q), qW);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  const optLines = item.options.map((o, i) =>
    pdf.splitTextToSize(`${String.fromCharCode(65 + i)}. ${pdfSafe(o)}`, qW - 24));
  pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8.5);
  const expLines = item.explanation ? pdf.splitTextToSize(`Why: ${pdfSafe(item.explanation)}`, qW) : [];
  const LH_Q = 4.6, LH_O = 4.2, LH_E = 3.9;
  const height = 6 + qLines.length * LH_Q + 1.5
    + optLines.reduce((h, l) => h + l.length * LH_O + 0.8, 0)
    + (expLines.length ? 1.5 + expLines.length * LH_E : 0) + 4;
  return { qLines, optLines, expLines, height, LH_Q, LH_O, LH_E };
};

const drawBlock = (pdf, item, b, y) => {
  const status = item.picked === null ? 'skipped' : item.picked === item.correct ? 'correct' : 'wrong';
  const colors = { correct: [34, 197, 94], wrong: [239, 68, 68], skipped: [161, 161, 170] };
  const [r, g, bl] = colors[status];
  pdf.setFillColor(r, g, bl);
  pdf.rect(M, y, 1.2, b.height - 4, 'F');

  const x = M + 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
  pdf.setTextColor(r, g, bl);
  pdf.text(`Q${item.n}  ${status.toUpperCase()}`, x, y + 3);
  pdf.setTextColor(130, 130, 130);
  pdf.setFont('helvetica', 'normal');
  pdf.text(pdfSafe(item.category), PAGE_W - M, y + 3, { align: 'right' });

  let cy = y + 6 + 3.2;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(20, 20, 20);
  b.qLines.forEach((l) => { pdf.text(l, x, cy); cy += b.LH_Q; });
  cy += 1.5;

  pdf.setFontSize(9);
  b.optLines.forEach((lines, i) => {
    const isCorrect = i === item.correct;
    const isPicked = i === item.picked;
    pdf.setFont('helvetica', isCorrect || isPicked ? 'bold' : 'normal');
    if (isCorrect) pdf.setTextColor(22, 140, 70);
    else if (isPicked) pdf.setTextColor(200, 40, 40);
    else pdf.setTextColor(70, 70, 70);
    lines.forEach((l, li) => {
      pdf.text(l, x + 2, cy);
      if (li === 0 && (isCorrect || isPicked)) {
        const tag = isCorrect && isPicked ? 'YOUR ANSWER - CORRECT' : isCorrect ? 'CORRECT' : 'YOUR ANSWER';
        pdf.setFontSize(7);
        pdf.text(tag, PAGE_W - M, cy, { align: 'right' });
        pdf.setFontSize(9);
      }
      cy += b.LH_O;
    });
    cy += 0.8;
  });

  if (b.expLines.length) {
    cy += 1.5;
    pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8.5); pdf.setTextColor(100, 100, 100);
    b.expLines.forEach((l) => { pdf.text(l, x, cy); cy += b.LH_E; });
  }
};

export const downloadPDF = async (reportData) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const canvas = renderReportToCanvas(reportData);
  pdf.setFillColor(5, 5, 5);
  pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');
  const imgH = (canvas.height / canvas.width) * PAGE_W;
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, (PAGE_H - imgH) / 2, PAGE_W, imgH, undefined, 'FAST');

  const review = reportData.review || [];
  if (review.length) {
    pdf.addPage();
    drawReviewPage(pdf, reportData);
    let y = TOP;
    review.forEach((item) => {
      const b = buildBlock(pdf, item);
      if (y + b.height > BOTTOM) {
        pdf.addPage();
        drawReviewPage(pdf, reportData);
        y = TOP;
      }
      drawBlock(pdf, item, b, y);
      y += b.height;
    });
  }

  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(140, 140, 140);
    pdf.text(`Page ${p} of ${pages}`, PAGE_W / 2, PAGE_H - 8, { align: 'center' });
  }
  pdf.save(`Final_Assessment_${(reportData.trackLabel || 'Java').replace('++', 'PP')}_${(reportData.learnerName || 'Learner').replace(/\s+/g, '_')}.pdf`);
};

// Grade calculator
export const gradeFromPercent = (p) => {
  if (p >= 90) return 'A+';
  if (p >= 80) return 'A';
  if (p >= 70) return 'B+';
  if (p >= 60) return 'B';
  if (p >= 50) return 'C';
  if (p >= 40) return 'D';
  return 'F';
};
